import React, { useState, useEffect, useCallback } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import repositoryContainer from '../../../services/RepositoryContainer';
import Image from '../../../components/AppImage';
import { useToast } from '../../../components/context/ToastContext';

const StorefrontCard = ({ companyPath, companyName, addToast }) => {
  const [copied, setCopied]   = useState(false);
  const [showQR, setShowQR]   = useState(false);

  const url    = companyPath ? `${window.location.origin}/${companyPath}` : null;
  const qrSrc  = url ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(url)}&bgcolor=ffffff&color=000000&margin=12` : null;
  const qrLarge = url ? `https://api.qrserver.com/v1/create-qr-code/?size=600x600&data=${encodeURIComponent(url)}&bgcolor=ffffff&color=000000&margin=20` : null;

  const handleCopy = () => {
    if (!url) return;
    navigator.clipboard.writeText(url).then(() => {
      setCopied(true);
      addToast('Storefront URL copied!', 'success');
      setTimeout(() => setCopied(false), 2000);
    });
  };

  if (!url) return null;

  return (
    <div className="pt-4 mt-1 border-t border-border space-y-3">
      <h4 className="text-xs font-bold uppercase tracking-widest text-primary">Link</h4>
      <p className="text-sm text-muted-foreground font-caption">
        Share this link so clients can browse your storefront.
      </p>

      {/* URL bar */}
      <div className="flex items-center gap-2">
        <div className="flex-1 flex items-center gap-2 bg-muted border border-border rounded-xl px-4 py-2.5 min-w-0">
          <Icon name="Globe" size={15} color="var(--color-muted-foreground)" className="flex-shrink-0" />
          <span className="text-sm font-caption text-foreground truncate">{url}</span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-border text-sm font-semibold font-caption text-foreground hover:bg-muted transition-colors flex-shrink-0"
        >
          <Icon name={copied ? 'Check' : 'Copy'} size={14} color={copied ? 'var(--color-primary)' : undefined} />
          {copied ? 'Copied' : 'Copy'}
        </button>
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="flex items-center gap-1.5 px-3 py-2.5 rounded-xl border border-border text-sm font-semibold font-caption text-foreground hover:bg-muted transition-colors flex-shrink-0"
        >
          <Icon name="ExternalLink" size={14} />
          Open
        </a>
      </div>

      {/* QR toggle */}
      <button
        onClick={() => setShowQR(p => !p)}
        className="flex items-center gap-2 text-sm font-semibold text-primary hover:opacity-80 transition-opacity font-caption"
      >
        <Icon name={showQR ? 'ChevronUp' : 'QrCode'} size={15} />
        {showQR ? 'Hide QR Code' : 'Show QR Code'}
      </button>

      {/* QR panel */}
      {showQR && (
        <div className="flex flex-col items-center gap-4 p-5 bg-muted/50 border border-border rounded-2xl animate-in fade-in slide-in-from-top-2 duration-200 sm:flex-row sm:items-start">
          <div className="flex-shrink-0 rounded-xl border border-border bg-white p-2 shadow-sm sm:w-auto">
            <img src={qrSrc} alt="Storefront QR code" width={120} height={120} className="block" />
          </div>
          <div className="flex-1 min-w-0 space-y-2 text-center sm:text-left">
            <p className="text-sm font-semibold text-foreground font-caption">
              {companyName ?? 'Your storefront'}
            </p>
            <p className="text-sm text-muted-foreground font-caption leading-relaxed">
              Clients can scan this code to go straight to your storefront. Print it, add it to your receipts, or share it on social media.
            </p>
            <a
              href={qrLarge}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 mt-2 w-full rounded-xl bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold hover:opacity-90 transition-opacity font-caption sm:w-auto"
            >
              <Icon name="Download" size={14} />
              Save QR Code
            </a>
          </div>
        </div>
      )}
    </div>
  );
};


const SOCIAL_PLATFORMS = [
  { key: 'facebook', label: 'Facebook', icon: 'bi bi-facebook', placeholder: 'facebook.com/yourpage', apiKey: 'facebookUrl' },
  { key: 'instagram', label: 'Instagram', icon: 'bi bi-instagram', placeholder: 'instagram.com/yourprofile', apiKey: 'instagramUrl' },
  { key: 'twitter', label: 'X (Twitter)', icon: 'bi bi-twitter-x', placeholder: 'twitter.com/yourhandle', apiKey: 'twitterUrl' },
  { key: 'linkedin', label: 'LinkedIn', icon: 'bi bi-linkedin', placeholder: 'linkedin.com/company/...', apiKey: 'linkedInUrl' },
  { key: 'snapchat', label: 'Snapchat', icon: 'bi bi-snapchat', placeholder: 'snapchat.com/add/...', apiKey: 'snapchat' },
  { key: 'website', label: 'Website', icon: 'bi bi-globe2', placeholder: 'yourwebsite.com', apiKey: 'websiteUrl' },
];

const IdentityHub = ({ companyData, setCompanyData }) => {
  const [companyFormData, setCompanyFormData] = useState({
    name: '',
    address: '',
    phoneNumber: '',
    description: '',
    logoUrl: '',
  });

  const [socialFormData, setSocialFormData] = useState({
    facebook: '',
    instagram: '',
    twitter: '',
    linkedin: '',
    snapchat: '',
    website: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [companyHasChanges, setCompanyHasChanges] = useState(false);
  const [socialHasChanges, setSocialHasChanges] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [showLogoUrlInput, setShowLogoUrlInput] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const { addToast } = useToast();

  const mapDataToState = useCallback((data) => {
    if (!data) return;
    const source = data.company ? data.company : data;

    setCompanyFormData({
      name: source.name || '',
      address: source.address || '',
      phoneNumber: source.phoneNumber || '',
      description: source.description || '',
      logoUrl: source.logoUrl || source.imageUrl || '',
    });

    const socialMap = {};
    SOCIAL_PLATFORMS.forEach((p) => {
      socialMap[p.key] = source[p.apiKey] || '';
    });
    setSocialFormData(socialMap);
  }, []);

  useEffect(() => {
    mapDataToState(companyData);
    setCompanyHasChanges(false);
    setSocialHasChanges(false);
    setImageError(false);
  }, [companyData, mapDataToState]);

  const handleCompanyInputChange = (e) => {
    const { name, value } = e.target;
    setCompanyFormData((prev) => ({ ...prev, [name]: value }));
    setCompanyHasChanges(true);
    setMessage(null);
  };

  const handleSocialInputChange = (e) => {
    const { name, value } = e.target;
    setSocialFormData((prev) => ({ ...prev, [name]: value }));
    setSocialHasChanges(true);
    setMessage(null);
  };

  const handleLogoUpload = async (e) => {
  const file = e.target.files?.[0];
  if (!file) return;

  setIsUploading(true);
  try {
    
    const uploadedUrl = await repositoryContainer.file.upload(file, 'logos');
    
   
    setCompanyFormData((prev) => ({ ...prev, logoUrl: uploadedUrl }));
    setCompanyHasChanges(true);
    setImageError(false);
  } catch (err) {
    addToast('Failed to upload logo.', 'error');  
    setMessage({ type: 'error', text: 'Failed to upload logo.' });
  } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = async () => {
    if (!companyHasChanges && !socialHasChanges) return;
    try {
      setLoading(true);
      setMessage(null);

      const socialPayload = {};
      SOCIAL_PLATFORMS.forEach(p => {
        socialPayload[p.apiKey] = socialFormData[p.key] || "";
      });

      await Promise.all([
        companyHasChanges ? repositoryContainer.company.updateCompanyDetails(companyFormData) : Promise.resolve(),
        socialHasChanges ? repositoryContainer.company.updateSocialMediaLinks(socialPayload) : Promise.resolve(),
      ]);

      if (typeof setCompanyData === 'function') {
        // We pass the updated data back to context
        setCompanyData((prev) => ({
          ...prev,
          ...(prev?.company ? { company: { ...prev.company, ...companyFormData, ...socialPayload } } : { ...companyFormData, ...socialPayload })
        }));
      }

      setCompanyHasChanges(false);
      setSocialHasChanges(false);
      setMessage({ type: 'success', text: 'Identity updated successfully!' });
      setTimeout(() => setMessage(null), 5000);
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error?.response?.data?.message || error?.data?.message || 'Failed to update' 
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="settings-bento-card">
      <div className="settings-card-header">
        <h3 className="settings-card-title">Brand Identity</h3>
        <p className="settings-card-description">Manage your company details and social media presence.</p>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        {/* ── Company Profile ── */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-widest text-primary">Company Profile</h4>
          
          <div className="flex flex-col md:flex-row gap-4 items-center md:items-start">
            {/* Logo Wrapper with Hover Indicator Fix */}
            {/* Replace the Logo Wrapper div starting at line 105 */}
                <label className="relative w-28 h-28 md:w-32 md:h-32 rounded-lg overflow-hidden bg-muted flex-shrink-0 group cursor-pointer border border-border mx-auto md:mx-0">
                  <input 
                    type="file" 
                    className="hidden" 
                    accept="image/*" 
                    onChange={handleLogoUpload} 
                    disabled={isUploading}
                  />
                  
                  {isUploading ? (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    </div>
                  ) : !imageError && companyFormData.logoUrl ? (
                    <Image
                      src={companyFormData.logoUrl}
                      alt="Company logo"
                      className="w-full h-full object-cover"
                      onError={() => setImageError(true)}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Icon name="ImageOff" size={32} color="var(--color-muted-foreground)" />
                    </div>
                  )}
                  
                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-200 z-10">
                    <Icon name="Edit" size={20} color="white" className="mb-1" />
                    <p className="text-[10px] text-white font-bold uppercase tracking-tighter">
                      {isUploading ? 'Uploading...' : 'Change Logo'}
                    </p>
                  </div>
                </label>

            <div className="flex-1 w-full space-y-4">
               <div  className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                  <i className="bi bi-buildings" size={16} />
                </div>
              <Input name="name" value={companyFormData.name} onChange={handleCompanyInputChange} placeholder="Company Name" className="border-none pl-10" />
              </div>
              <div  className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                  <i className="bi bi-telephone" size={16} />
                </div>
              <Input name="phoneNumber" value={companyFormData.phoneNumber} onChange={handleCompanyInputChange} placeholder="Phone Number" className="border-none pl-10" />
              </div>
              <div  className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                  <i className="bi bi-geo-alt" size={16} />
                </div>
              <Input name="address" value={companyFormData.address} onChange={handleCompanyInputChange} placeholder="Address" className="border-none pl-10" />
              </div>
               <div  className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                  <i className="bi bi-chat-square-quote" size={16} />
                </div>
              <Input name="description" value={companyFormData.description} onChange={handleCompanyInputChange} placeholder="Description" className="border-none pl-10" />
              </div>
              
              {showLogoUrlInput && (
                <div className="animate-in fade-in slide-in-from-top-1 duration-200">
                  <Input
                    name="logoUrl"
                    value={companyFormData.logoUrl}
                    onChange={(e) => { handleCompanyInputChange(e); setImageError(false); }}
                    placeholder="Paste logo image URL"
                    className="border-primary/40 ring-1 ring-primary/20"
                    autoFocus
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Social Connections ── */}
        <div className="space-y-4">
          <h4 className="text-xs font-bold uppercase tracking-widest text-primary">Social Connections</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {SOCIAL_PLATFORMS.map(({ key, label, icon, placeholder }) => (
              <div key={key} className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground z-10">
                  <i className={icon} size={16} />
                </div>
                <Input
                  name={key}
                  value={socialFormData[key]}
                  onChange={handleSocialInputChange}
                  placeholder={placeholder || label}
                  className="pl-10"
                />
              </div>
            ))}
          </div>

          <StorefrontCard
            companyPath={companyData?.companyPath}
            companyName={companyFormData.name || companyData?.name}
            addToast={addToast}
          />
        </div>
      </div>

      {message && (
        <div className={`text-sm mt-6 p-3 rounded-md border animate-in fade-in zoom-in-95 duration-200 ${
          message.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : 'bg-red-50 border-red-200 text-destructive'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex justify-end gap-3 pt-5 border-t mt-5">
        {(companyHasChanges || socialHasChanges) && (
          <Button variant="ghost" onClick={() => mapDataToState(companyData)} disabled={loading}>
            Discard Changes
          </Button>
        )}
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={!(companyHasChanges || socialHasChanges) || loading}
        >
          {loading ? 'Saving...' : 'Update Identity'}
        </Button>
      </div>
    </div>
  );
};

export default IdentityHub;